import { formatMoney } from "../../../../packages/pvc-product/pvc-product-page-view-model";
import { PVC_SIGN_ROUTE_PATH } from "../../../../packages/pvc-product/pvc-sign-route";
import {
  PRODUCT_CATALOG_PATH,
  PRODUCT_PAGE_PREFIX,
  PRODUCT_PAGES,
  productPagePath,
} from "../product-pages/product-pages-content";
import type { PublicCatalogItem } from "../product-pages/public-catalog-api";
import { FEATURED_PRODUCTS } from "./homepage-content";
import { HomepageProductPhoto } from "./HomepageProductPhoto";
import { useHomepagePvcQuote } from "./use-homepage-pvc-quote";

export function FeaturedProducts({
  publicCatalog = null,
}: {
  publicCatalog?: PublicCatalogItem[] | null;
}) {
  const { state } = useHomepagePvcQuote(60, 40, 1);
  const publicBySlug = new Map((publicCatalog ?? []).map((item) => [item.slug, item]));
  type FeaturedCard = (typeof FEATURED_PRODUCTS)[number] & {
    imageUrl?: string | null;
    imageWidth?: number | null;
    imageHeight?: number | null;
  };
  const products: FeaturedCard[] = publicCatalog == null || publicCatalog.length === 0
    ? [...FEATURED_PRODUCTS]
    : [
        FEATURED_PRODUCTS.find((product) => product.id === "product-pvc-sign")!,
        ...publicCatalog
          .filter((item) =>
            item.slug !== "pvc-sign"
            && item.homepageVisible
            && (item.featured || item.bestseller),
          )
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((item): FeaturedCard => {
            const product = PRODUCT_PAGES.find((candidate) => candidate.slug === item.slug);
            if (product) {
              return {
                id: `product-${product.slug}`,
                name: product.title,
                href: productPagePath(product.slug),
                ctaLabel: item.quoteOnly || !item.orderableOnline
                  ? "לקבלת הצעת מחיר"
                  : "לבניית מפרט וקבלת מחיר",
                visual: product.visual,
                imageUrl: item.featuredAssetPreview ?? null,
                imageWidth: item.featuredAssetWidth ?? null,
                imageHeight: item.featuredAssetHeight ?? null,
              };
            }
            return {
              id: `product-${item.slug}`,
              name: item.name?.trim() || item.slug,
              href: `${PRODUCT_PAGE_PREFIX}${encodeURIComponent(item.slug)}`,
              ctaLabel: item.quoteOnly || !item.orderableOnline
                ? "לקבלת הצעת מחיר"
                : "לבניית מפרט וקבלת מחיר",
              visual: "sign-mockup",
              imageUrl: item.featuredAssetPreview ?? null,
              imageWidth: item.featuredAssetWidth ?? null,
              imageHeight: item.featuredAssetHeight ?? null,
            };
          }),
      ];
  return (
    <section
      className="hp-section hp-featured"
      id="best-sellers"
      aria-labelledby="hp-bestsellers-title"
    >
      <div className="hp-section-heading">
        <div>
          <span className="hp-eyebrow">מהרעיון, ישר לדפוס</span>
          <h2 id="hp-bestsellers-title">מוצרים נבחרים</h2>
        </div>
        <a className="hp-section-action" href={PRODUCT_CATALOG_PATH}>
          כל הקטגוריות <span aria-hidden="true">←</span>
        </a>
      </div>
      <div className="hp-products-grid">
        {products.map((product) => {
          const pvc = product.id === "product-pvc-sign";
          const slug = !pvc && product.href.startsWith(PRODUCT_PAGE_PREFIX)
            ? product.href.slice(PRODUCT_PAGE_PREFIX.length)
            : null;
          const managed = slug ? publicBySlug.get(slug) : null;
          const pvcQuote = pvc && state.status === "ready" ? state.quote : null;
          const loading =
            pvc
            && (state.status === "initial-loading" || state.status === "recalculating");
          return (
            <article
              key={product.id}
              id={product.id}
              className="hp-product-card"
            >
              {product.imageUrl ? (
                <img
                  className="hp-product-thumb hp-homepage-server-product-image"
                  src={product.imageUrl}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                  width={product.imageWidth ?? undefined}
                  height={product.imageHeight ?? undefined}
                />
              ) : (
                <HomepageProductPhoto
                  visual={product.visual}
                  alt={product.name}
                />
              )}
              <div className="hp-product-content">
                <h3>{product.name}</h3>
                <p className="hp-product-spec">
                  {pvc
                    ? "60×40 ס״מ · PVC בעובי 3 מ״מ"
                    : "מידה, כמות וגימור לבחירתכם"}
                </p>
                <div className="hp-product-price" aria-live="polite">
                  {pvcQuote ? (
                    <>
                      <strong>
                        <bdi>{formatMoney(pvcQuote.totalMinor, "ILS")}</bdi>
                      </strong>
                      <small>
                        {pvcQuote.includesVat ? "כולל מע״מ" : "לפני מע״מ"} · ללא
                        משלוח
                      </small>
                    </>
                  ) : (
                    <span className="hp-price-unavailable">
                      {loading
                        ? "טוענים מחיר…"
                        : pvc
                          ? "המחיר אינו זמין כרגע"
                          : managed && (managed.quoteOnly || !managed.orderableOnline)
                            ? "הצעת מחיר לפי המפרט"
                            : "מחיר לפי המפרט בעמוד המוצר"}
                    </span>
                  )}
                </div>
                {pvc ? (
                  <a
                    className="hp-btn hp-btn-primary"
                    href={`${PVC_SIGN_ROUTE_PATH}?width=60&height=40&quantity=1`}
                  >
                    לבחירת מידה <span aria-hidden="true">←</span>
                  </a>
                ) : (
                  <a
                    className="hp-btn hp-btn-secondary"
                    href={product.href}
                  >
                    {product.ctaLabel} <span aria-hidden="true">←</span>
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
